import * as React from "react";

type AutoFocusHandler = (event: Event) => void;

/** The controls most recently focused or pressed, newest first. */
const recent: HTMLElement[] = [];
const FOCUSABLE = "a[href], button, input, select, textarea, [tabindex]";

function remember(element: HTMLElement | null) {
  if (!element || element === document.body) return;
  const index = recent.indexOf(element);
  if (index !== -1) recent.splice(index, 1);
  recent.unshift(element);
  recent.length = Math.min(recent.length, 10);
}

if (typeof document !== "undefined") {
  document.addEventListener(
    "focusin",
    (event) => {
      if (event.target instanceof HTMLElement) remember(event.target);
    },
    true,
  );
  // A press as well as a focus: Safari does not focus a button on click, so
  // a dialogue opened with the mouse there would otherwise return focus to
  // whatever the keyboard last touched.
  document.addEventListener(
    "pointerdown",
    (event) => {
      if (event.target instanceof Element) {
        const control = event.target.closest(FOCUSABLE);
        if (control instanceof HTMLElement) remember(control);
      }
    },
    true,
  );
}

/**
 * Returns focus to the control that opened a modal, once it closes
 * (Jira NAV-28, WCAG 2.4.3).
 *
 * Radix's modal content sends focus back to its `DialogTrigger`, and nowhere
 * else. Most dialogues here are opened by an ordinary button that sets state,
 * so there is no trigger and focus falls to `<body>`, throwing a keyboard or
 * screen-reader user to the top of the document.
 *
 * The opener is the control most recently focused or pressed outside the
 * content, read when the content's element mounts — which is when the
 * dialogue opens, whether it is mounted conditionally or rendered with
 * `open={x}` all along. Neither the wrapper's render nor Radix's
 * open-autofocus event is that moment: the wrapper renders while closed, and
 * Radix skips the event when a field inside takes focus with `autoFocus`. For
 * a dialogue with a `DialogTrigger` the opener is that trigger, so this and
 * Radix agree. A caller's `onCloseAutoFocus` runs first and keeps the last
 * word: if it prevents the default, nothing here moves focus.
 */
export function useReturnFocus(onCloseAutoFocus?: AutoFocusHandler): {
  contentRef: (node: HTMLElement | null) => void;
  onCloseAutoFocus: AutoFocusHandler;
} {
  const opener = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useCallback((node: HTMLElement | null) => {
    if (!node) return;
    opener.current =
      recent.find((element) => element.isConnected && !node.contains(element)) ??
      null;
  }, []);
  const handleClose = React.useCallback<AutoFocusHandler>(
    (event) => {
      onCloseAutoFocus?.(event);
      const target = opener.current;
      opener.current = null;
      if (event.defaultPrevented || !target?.isConnected) return;
      event.preventDefault();
      target.focus();
    },
    [onCloseAutoFocus],
  );
  return { contentRef, onCloseAutoFocus: handleClose };
}

/** One ref from several, for a component that needs its own handle on an
 *  element it also forwards. */
export function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<T | null>).current = node;
    }
  };
}
